/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Error codes", description = "A reference of all Hightouch error codes, what they mean, and how to resolve them.", lastUpdatedDate = new Date(1784678400000), keywords = "error, error code, sync failed, sync error, troubleshooting, rejected rows, rate limit, invalid credentials, invalid grant, authentication failed, destination error, predictive traits error, primary key, duplicate columns";
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Error codes",
        description: "A reference of all Hightouch error codes, what they mean, and how to resolve them.",
        lastUpdatedDate: "Wed Jul 22 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Sync request errors",
          "depth": 0,
          "id": "sync-request-errors"
        }, {
          "value": "Destination errors",
          "depth": 0,
          "id": "destination-errors"
        }, {
          "value": "Generic destination errors",
          "depth": 1,
          "id": "generic-destination-errors"
        }, {
          "value": "Salesforce",
          "depth": 1,
          "id": "salesforce"
        }, {
          "value": "Salesforce Marketing Cloud (SFMC)",
          "depth": 1,
          "id": "salesforce-marketing-cloud-sfmc"
        }, {
          "value": "HubSpot",
          "depth": 1,
          "id": "hubspot"
        }, {
          "value": "Google Ads",
          "depth": 1,
          "id": "google-ads"
        }, {
          "value": "Braze",
          "depth": 1,
          "id": "braze"
        }, {
          "value": "TikTok",
          "depth": 1,
          "id": "tiktok"
        }, {
          "value": "Google Sheets",
          "depth": 1,
          "id": "google-sheets"
        }, {
          "value": "Slack",
          "depth": 1,
          "id": "slack"
        }, {
          "value": "Other destinations",
          "depth": 1,
          "id": "other-destinations"
        }, {
          "value": "Rejected-row errors",
          "depth": 0,
          "id": "rejected-row-errors"
        }, {
          "value": "LinkedIn",
          "depth": 1,
          "id": "linkedin"
        }, {
          "value": "Predictive traits errors",
          "depth": 0,
          "id": "predictive-traits-errors"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      code: "code",
      a: "a",
      strong: "strong",
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      h3: "h3"
    }, _provideComponents(), props.components), {PylonLink} = _components;
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: "When something fails in Hightouch, the platform assigns an error code that identifies the cause. This page is a complete reference of all Hightouch error codes, organized by where they originate."
      }), "\n", _jsx(_components.p, {
        children: "Some error codes include troubleshooting links directly in the Hightouch UI. If you don't see a link for your error, use this page to find the code and follow the resolution steps."
      }), "\n", _jsxs(Info, {
        children: [_jsxs(_components.p, {
          children: ["Don't see your error code? This page covers Hightouch platform error codes only. Hightouch passes error messages from third-party APIs directly, so if you see a vendor error (like a ", _jsx(_components.code, {
            children: "400"
          }), " or ", _jsx(_components.code, {
            children: "409"
          }), "), check the ", _jsx(_components.a, {
            href: "/destinations/overview",
            children: "destination's docs page"
          }), " under ", _jsx(_components.strong, {
            children: "Tips and troubleshooting"
          }), " or use the ", _jsx(_components.a, {
            href: "/syncs/debugger",
            children: "live debugger"
          }), " to inspect the full response."]
        }), _jsx("br", {}), _jsxs(_components.p, {
          children: ["If your error still isn't covered anywhere, use the ", _jsx(_components.strong, {
            children: "Send feedback"
          }), " button on this page to let us know and we'll add it."]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "sync-request-errors",
        children: "Sync request errors"
      }), "\n", _jsx(_components.p, {
        children: "These errors occur during sync planning, before Hightouch sends any data to the destination. They typically indicate a problem with your model, primary key, or warehouse configuration."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Error code"
            }), _jsx(_components.th, {
              align: "left",
              children: "What it means"
            }), _jsx(_components.th, {
              align: "left",
              children: "How to resolve"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "DUPLICATE_COLUMNS_DETECTED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Your model query returns duplicate column names."
            }), _jsx(_components.td, {
              align: "left",
              children: "Alias columns in your model SQL so every selected field has a unique name."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "NON_UNIQUE_PRIMARY_KEY"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The selected primary key contains duplicate values."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Update your model so each row has a unique identifier. If you're using a SQL model, add a ", _jsx(_components.code, {
                children: "DISTINCT"
              }), " clause or ", _jsx(_components.code, {
                children: "GROUP BY"
              }), " on the key column."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "UNSUPPORTED_PRIMARY_KEY_TYPE"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The primary key's data type isn't supported for this sync path."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Cast the primary key to a supported type (typically ", _jsx(_components.code, {
                children: "VARCHAR"
              }), " or ", _jsx(_components.code, {
                children: "INTEGER"
              }), ") in your model SQL."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "QUERY_COLUMN_MISSING"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "A column referenced in your sync configuration no longer exists in the model query results."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Refresh the model schema in Hightouch and update your ", _jsx(_components.a, {
                href: "/syncs/mapping-data",
                children: "field mappings"
              }), " to use valid columns."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "WAREHOUSE_TABLE_MISSING"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "A warehouse table required for sync planning doesn't exist."
            }), _jsx(_components.td, {
              align: "left",
              children: "Confirm the table still exists in your warehouse and that the model points to the correct schema and table name."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "PREVIOUS_SYNC_RUN_OBJECT_MISSING"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Stored objects from the previous sync run are missing, usually because cached plan data expired."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Retry the sync. If it persists, trigger a ", _jsx(_components.a, {
                href: "/syncs/overview#resync-full-query",
                children: "full resync"
              }), " to rebuild sync state."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "REMOVE_PLAN_INCOMPLETE"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Backfill or removal planning couldn't complete during a planner transition."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Retry the sync after a few minutes. If it continues, ", _jsx(PylonLink, {
                children: "contact support"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "REMOVE_RETRY_CHANGED_COLUMN_TYPES"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Rejected-row removal retries are unsafe because model column types changed between runs."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Refresh your model schema and trigger a ", _jsx(_components.a, {
                href: "/syncs/overview#resync-full-query",
                children: "full resync"
              }), " from a consistent state."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SORT_RAN_OUT_OF_DISK_SPACE"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Sorting rows exhausted available disk space."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Narrow the model to reduce row volume, or ", _jsx(PylonLink, {
                children: "contact support"
              }), " for infrastructure help."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "UNSPECIFIED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Hightouch couldn't classify the failure into a more specific code."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Use the ", _jsx(_components.a, {
                href: "/syncs/debugger",
                children: "live debugger"
              }), " and ", _jsx(_components.a, {
                href: "/syncs/warehouse-sync-logs",
                children: "warehouse sync logs"
              }), " to identify the root cause."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "destination-errors",
        children: "Destination errors"
      }), "\n", _jsx(_components.h3, {
        id: "generic-destination-errors",
        children: "Generic destination errors"
      }), "\n", _jsx(_components.p, {
        children: "These errors can occur with any destination. They indicate a problem with authentication, rate limits, or the destination's availability."
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Error code"
            }), _jsx(_components.th, {
              align: "left",
              children: "What it means"
            }), _jsx(_components.th, {
              align: "left",
              children: "How to resolve"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "DESTINATION_INVALID_GRANT"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The destination's authorization grant expired or was revoked."
            }), _jsx(_components.td, {
              align: "left",
              children: "Reconnect the destination in Hightouch to refresh authorization."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "DESTINATION_INVALID_CREDENTIALS"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The destination credentials are invalid."
            }), _jsx(_components.td, {
              align: "left",
              children: "Verify the username, password, token, or secret configured for the destination. Reconnect if needed."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "DESTINATION_INVALID_API_KEY"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The API key is invalid or expired."
            }), _jsx(_components.td, {
              align: "left",
              children: "Replace it with a current key that has the required permissions."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "DESTINATION_DEFINITION_NOT_FOUND"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Hightouch couldn't load the destination type definition."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Confirm you're using a supported destination type. If the connector appears broken, ", _jsx(PylonLink, {
                children: "contact support"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "DESTINATION_INTERNAL_ERROR"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The destination returned a generic internal failure or timed out."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Retry the sync. If it continues, check the destination's status page for outages, or ", _jsx(PylonLink, {
                children: "contact support"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "DESTINATION_RATE_LIMIT"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The destination rate-limited the sync."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Wait for the rate limit window to reset, then retry. To prevent recurrence, reduce ", _jsx(_components.a, {
                href: "/syncs/schedule-sync-ui",
                children: "sync frequency"
              }), " or batch volume."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "DESTINATION_MISSING_AD_ACCOUNT"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "A required ad account is missing from the destination configuration or inaccessible."
            }), _jsx(_components.td, {
              align: "left",
              children: "Re-check the destination setup and confirm your account has access to the selected ad account."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "DESTINATION_PAYLOAD_TOO_LARGE"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The destination rejected the payload as too large."
            }), _jsx(_components.td, {
              align: "left",
              children: "Reduce batch size in your sync configuration, or trim the number of mapped fields."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "salesforce",
        children: "Salesforce"
      }), "\n", _jsxs(_components.p, {
        children: ["For additional Salesforce-specific errors returned by the Salesforce API, see ", _jsx(_components.a, {
          href: "/destinations/salesforce#tips-and-troubleshooting",
          children: "Salesforce: Tips and troubleshooting"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Error code"
            }), _jsx(_components.th, {
              align: "left",
              children: "What it means"
            }), _jsx(_components.th, {
              align: "left",
              children: "How to resolve"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SALESFORCE_INACTIVE_USER"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The Salesforce integration user is inactive."
            }), _jsx(_components.td, {
              align: "left",
              children: "Reconnect the destination with an active Salesforce user."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SALESFORCE_EXPIRED_TOKEN"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The Salesforce access token expired."
            }), _jsxs(_components.td, {
              align: "left",
              children: [_jsx(_components.a, {
                href: "/destinations/salesforce#connect-to-salesforce",
                children: "Reauthorize the destination"
              }), " in Hightouch."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SALESFORCE_INACTIVE_ORG"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The Salesforce org is inactive or inaccessible."
            }), _jsx(_components.td, {
              align: "left",
              children: "Confirm the org is active and the integration user can log in."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SALESFORCE_RESOURCE_DOES_NOT_EXIST"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "A referenced Salesforce object, field, or record doesn't exist."
            }), _jsx(_components.td, {
              align: "left",
              children: "Check the object name, field name, or record ID in your sync configuration."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SALESFORCE_AUTH_FAILURE"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Salesforce authentication failed."
            }), _jsx(_components.td, {
              align: "left",
              children: "Re-check credentials, connected app settings, and any IP or login restrictions in Salesforce."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SALESFORCE_REQUESTS_LIMIT_EXCEEDED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Salesforce API limits were exceeded."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Wait for the limit to reset, or reduce sync frequency and concurrency. See ", _jsx(_components.a, {
                href: "https://developer.salesforce.com/docs/atlas.en-us.salesforce_app_limits_cheatsheet.meta/salesforce_app_limits_cheatsheet/salesforce_app_limits_platform_api.htm",
                children: "Salesforce API limits"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SALESFORCE_NETWORK_DIFFICULTY"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "A network or transport failure prevented the Salesforce request from completing."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Retry the sync and check for temporary Salesforce outages on ", _jsx(_components.a, {
                href: "https://status.salesforce.com/",
                children: "Salesforce Status"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SALESFORCE_MAX_CALL_STACK"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Salesforce hit a recursion or call-stack limit during processing."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Review Apex triggers, flows, and automation rules that may be recursively updating records during the sync. See ", _jsx(_components.a, {
                href: "/destinations/salesforce#unable_to_lock_row",
                children: "Salesforce: UNABLE_TO_LOCK_ROW"
              }), " for related guidance."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SALESFORCE_UNABLE_TO_LOCK_ROW"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Salesforce row locking prevented the write because another process is modifying the same record."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Retry later, reduce concurrent updates, or lower your ", _jsx(_components.a, {
                href: "/destinations/salesforce#batching-and-parallelization",
                children: "parallelization setting"
              }), ". See ", _jsx(_components.a, {
                href: "/destinations/salesforce#unable_to_lock_row",
                children: "Salesforce: UNABLE_TO_LOCK_ROW"
              }), " for detailed steps."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "salesforce-marketing-cloud-sfmc",
        children: "Salesforce Marketing Cloud (SFMC)"
      }), "\n", _jsxs(_components.p, {
        children: ["For additional SFMC-specific errors, see ", _jsx(_components.a, {
          href: "/destinations/sfmc#tips-and-troubleshooting",
          children: "SFMC: Tips and troubleshooting"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Error code"
            }), _jsx(_components.th, {
              align: "left",
              children: "What it means"
            }), _jsx(_components.th, {
              align: "left",
              children: "How to resolve"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SFMC_DATA_EXTENSION_ADD_UPDATE_ONLY"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "This SFMC sync path only supports add and update operations."
            }), _jsx(_components.td, {
              align: "left",
              children: "Change the sync mode or target a Data Extension that supports the requested operation."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SFMC_IMPORT_FAILED_NO_RECORDS"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The SFMC import failed because no records were present in the batch."
            }), _jsx(_components.td, {
              align: "left",
              children: "Confirm that the source model returned rows for this sync run."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SFMC_INVALID_CLIENT_SECRET"
              })
            }), _jsxs(_components.td, {
              align: "left",
              children: ["The installed package's client secret expired or is invalid, so SFMC rejected authentication. ", _jsx(_components.a, {
                href: "https://developer.salesforce.com/docs/marketing/marketing-cloud/guide/rotate-oauth2-secret.html",
                children: "Client secrets expire 180 days after they're generated"
              }), "."]
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Rotate the client secret in SFMC and update the destination configuration. See ", _jsx(_components.a, {
                href: "/destinations/sfmc#client-secret-rotation",
                children: "SFMC: Client secret rotation"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "hubspot",
        children: "HubSpot"
      }), "\n", _jsxs(_components.p, {
        children: ["For additional HubSpot-specific errors, see ", _jsx(_components.a, {
          href: "/destinations/hubspot#tips-and-troubleshooting",
          children: "HubSpot: Tips and troubleshooting"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Error code"
            }), _jsx(_components.th, {
              align: "left",
              children: "What it means"
            }), _jsx(_components.th, {
              align: "left",
              children: "How to resolve"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "HUBSPOT_RATE_LIMIT_DAILY"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "HubSpot's daily API limit was reached."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Wait for the daily limit to reset (typically midnight ET). To prevent recurrence, reduce sync frequency or volume. See ", _jsx(_components.a, {
                href: "https://developers.hubspot.com/docs/api/usage-details",
                children: "HubSpot API usage guidelines"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "HUBSPOT_RATE_LIMIT_TEN_SECONDLY"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "HubSpot's rolling 10-second rate limit was reached."
            }), _jsx(_components.td, {
              align: "left",
              children: "Hightouch automatically retries these. If the error persists across runs, reduce concurrent syncs to HubSpot."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "HUBSPOT_RATE_LIMIT_SECONDLY"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "HubSpot's per-second rate limit was reached."
            }), _jsx(_components.td, {
              align: "left",
              children: "Hightouch automatically retries these. Reduce burst volume if the error recurs."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "HUBSPOT_INVALID_REFRESH_TOKEN"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The HubSpot OAuth refresh token is invalid or expired."
            }), _jsx(_components.td, {
              align: "left",
              children: "Reconnect the HubSpot destination in Hightouch to generate a new token."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "HUBSPOT_SYNC_ERROR"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "HubSpot returned a generic sync error."
            }), _jsx(_components.td, {
              align: "left",
              children: "Retry the sync. If it continues, check HubSpot-side logs and object validation rules."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "HUBSPOT_CONNECTION_ERROR"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "A connection failure prevented the HubSpot request from completing."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Retry and check for temporary HubSpot outages on ", _jsx(_components.a, {
                href: "https://www.hubspotstatusupdates.com/",
                children: "HubSpot Status"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "HUBSPOT_CONTACT_ALREADY_EXISTS"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "A contact with this email already exists in HubSpot."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Review your record matching configuration. HubSpot uses email as the primary unique identifier. See ", _jsx(_components.a, {
                href: "/destinations/hubspot#409---contact-already-exists",
                children: "HubSpot: 409 - Contact already exists"
              }), " for detailed steps."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "HUBSPOT_CLOUDFLARE_BLOCKED_IP"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "HubSpot's Cloudflare proxy blocked the request IP."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["If you use ", _jsx(_components.a, {
                href: "/security/networking#ip-addresses",
                children: "Hightouch's static IPs"
              }), ", confirm they aren't blocked by your HubSpot account's network settings. Contact HubSpot support if needed."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "google-ads",
        children: "Google Ads"
      }), "\n", _jsxs(_components.p, {
        children: ["For additional Google Ads-specific errors, see ", _jsx(_components.a, {
          href: "/destinations/google#tips-and-troubleshooting",
          children: "Google Ads: Tips and troubleshooting"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Error code"
            }), _jsx(_components.th, {
              align: "left",
              children: "What it means"
            }), _jsx(_components.th, {
              align: "left",
              children: "How to resolve"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "GOOGLE_ADS_INTERNAL_SERVER_ERROR"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Google Ads returned an internal server error."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Retry the sync after a short delay. Check ", _jsx(_components.a, {
                href: "https://ads.google.com/status/publisher/",
                children: "Google Ads Status"
              }), " for outages."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "GOOGLE_ADS_USER_LIST_NOT_FOUND"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The target Google Ads audience doesn't exist."
            }), _jsx(_components.td, {
              align: "left",
              children: "Confirm the audience still exists in Google Ads and the connected account has access."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "GOOGLE_ADS_UNAUTHORIZED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Google Ads rejected the request due to missing permissions or invalid authorization."
            }), _jsx(_components.td, {
              align: "left",
              children: "Reauthorize the destination and confirm the connected account has the required access level."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "GOOGLE_ADS_USER_LIST_NAME_ALREADY_IN_USE"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "An audience with this name already exists in Google Ads."
            }), _jsx(_components.td, {
              align: "left",
              children: "Use a unique audience name, or connect the sync to the existing audience."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "GOOGLE_ADS_CONCURRENT_MODIFICATION"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Google Ads rejected the update because another change is still processing."
            }), _jsx(_components.td, {
              align: "left",
              children: "Wait a few minutes and retry. Avoid running multiple syncs to the same audience simultaneously."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "GOOGLE_ADS_STORE_SALES_DIRECT_DATA_NOT_ALLOWED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Store sales direct data isn't enabled for this Google Ads account."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Confirm account eligibility and enable the feature in Google Ads. See ", _jsx(_components.a, {
                href: "https://support.google.com/google-ads/answer/7620302",
                children: "Google Ads Store Sales requirements"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "GOOGLE_ADS_GENERIC_INVALID_ARGUMENT"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Google Ads rejected the request as invalid."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Review required fields, identifiers, and mapped values. Use the ", _jsx(_components.a, {
                href: "/syncs/debugger",
                children: "live debugger"
              }), " to inspect the rejected payload."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "braze",
        children: "Braze"
      }), "\n", _jsxs(_components.p, {
        children: ["For additional Braze-specific errors, see ", _jsx(_components.a, {
          href: "/destinations/braze#tips-and-troubleshooting",
          children: "Braze: Tips and troubleshooting"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Error code"
            }), _jsx(_components.th, {
              align: "left",
              children: "What it means"
            }), _jsx(_components.th, {
              align: "left",
              children: "How to resolve"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "BRAZE_SERVICE_UNAVAILABLE"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Braze is temporarily unavailable."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Retry the sync after a short delay. Check ", _jsx(_components.a, {
                href: "https://braze.statuspage.io/",
                children: "Braze Status"
              }), " for outages."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "BRAZE_ACCESS_DENIED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Braze denied access to the API."
            }), _jsx(_components.td, {
              align: "left",
              children: "Confirm the API key has the required permissions in your Braze dashboard."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "BRAZE_INVALID_REQUEST"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Braze rejected the request content or structure."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Check required fields and value formats. Use the ", _jsx(_components.a, {
                href: "/syncs/debugger",
                children: "live debugger"
              }), " to inspect the rejected payload."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "tiktok",
        children: "TikTok"
      }), "\n", _jsxs(_components.p, {
        children: ["For additional TikTok-specific errors, see ", _jsx(_components.a, {
          href: "/destinations/tiktok#tips-and-troubleshooting",
          children: "TikTok: Tips and troubleshooting"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Error code"
            }), _jsx(_components.th, {
              align: "left",
              children: "What it means"
            }), _jsx(_components.th, {
              align: "left",
              children: "How to resolve"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "TIKTOK_GATEWAY_TIMEOUT"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "TikTok timed out while processing the request."
            }), _jsx(_components.td, {
              align: "left",
              children: "Retry the sync after a short delay."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "TIKTOK_REJECTED_REPLACE_OP"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "TikTok rejected a replace operation because another audience job is still processing."
            }), _jsx(_components.td, {
              align: "left",
              children: "Wait for the existing job to complete before retrying."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "TIKTOK_AUDIENCE_ERROR"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The TikTok audience is invalid or not editable."
            }), _jsx(_components.td, {
              align: "left",
              children: "Confirm the audience exists in TikTok Ads Manager and supports the requested operation."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "TIKTOK_PERMISSION_DENIED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The connected TikTok account lacks the required permissions."
            }), _jsx(_components.td, {
              align: "left",
              children: "Reauthorize with a TikTok account that can manage the target advertiser and audience."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "TIKTOK_INVALID_ENCRYPTION_VALUE"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "TikTok rejected the configured encryption or hashing value."
            }), _jsx(_components.td, {
              align: "left",
              children: "Verify the expected hashing format in your TikTok sync configuration."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "google-sheets",
        children: "Google Sheets"
      }), "\n", _jsxs(_components.p, {
        children: ["For additional Google Sheets-specific errors, see ", _jsx(_components.a, {
          href: "/destinations/google-sheets#tips-and-troubleshooting",
          children: "Google Sheets: Tips and troubleshooting"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Error code"
            }), _jsx(_components.th, {
              align: "left",
              children: "What it means"
            }), _jsx(_components.th, {
              align: "left",
              children: "How to resolve"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "GOOGLE_SHEETS_INVALID_GRANT"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The Google Sheets authorization grant is invalid."
            }), _jsx(_components.td, {
              align: "left",
              children: "Reauthorize the destination in Hightouch."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "GOOGLE_SHEETS_INVALID_DATA_TYPE"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The sync tried to write an unsupported value type to a cell."
            }), _jsx(_components.td, {
              align: "left",
              children: "Cast complex values (arrays, objects) to plain text or supported scalar types in your model."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "GOOGLE_SHEETS_INVALID_RANGE"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The configured write range is invalid."
            }), _jsx(_components.td, {
              align: "left",
              children: "Check the tab name, cell range, and sheet structure in the destination configuration."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "GOOGLE_SHEETS_EXCEEDS_GRID_LIMITS"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The write would exceed the spreadsheet's grid limits (10 million cells)."
            }), _jsx(_components.td, {
              align: "left",
              children: "Reduce the number of rows or columns, or write to a new sheet."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "GOOGLE_SHEETS_MISSING_SHEET_ID"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The spreadsheet or sheet identifier is missing from the configuration."
            }), _jsx(_components.td, {
              align: "left",
              children: "Reopen the destination setup and confirm the selected sheet."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "GOOGLE_SHEETS_QUOTA_EXCEEDED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The Google Sheets API quota was exceeded."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Retry later, or reduce sync frequency. See ", _jsx(_components.a, {
                href: "https://developers.google.com/sheets/api/limits",
                children: "Google Sheets API limits"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "GOOGLE_SHEETS_ENTITY_NOT_FOUND"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The target spreadsheet or sheet no longer exists."
            }), _jsx(_components.td, {
              align: "left",
              children: "Confirm the spreadsheet exists and is shared with the connected Google account."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "slack",
        children: "Slack"
      }), "\n", _jsxs(_components.p, {
        children: ["For additional Slack-specific errors, see ", _jsx(_components.a, {
          href: "/destinations/slack#tips-and-troubleshooting",
          children: "Slack: Tips and troubleshooting"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Error code"
            }), _jsx(_components.th, {
              align: "left",
              children: "What it means"
            }), _jsx(_components.th, {
              align: "left",
              children: "How to resolve"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SLACK_NOT_IN_CHANNEL"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The Hightouch Slack app isn't a member of the target channel."
            }), _jsx(_components.td, {
              align: "left",
              children: "Invite the Hightouch app to the channel, or select a different channel."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SLACK_IS_ARCHIVED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The target Slack channel is archived."
            }), _jsx(_components.td, {
              align: "left",
              children: "Unarchive the channel or choose a different destination channel."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SLACK_INVALID_BLOCKS"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Slack rejected the block payload as invalid."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Validate the ", _jsx(_components.a, {
                href: "https://api.slack.com/tools/block-kit-builder",
                children: "Block Kit JSON"
              }), " structure and required fields."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SLACK_NO_TEXT"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The Slack message is missing required text content."
            }), _jsx(_components.td, {
              align: "left",
              children: "Add message text to your sync configuration, even if blocks are also present."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SLACK_NON_CALLABLE_ITERATOR"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "An unexpected payload construction failure occurred."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Retry once. If it persists, ", _jsx(PylonLink, {
                children: "contact support"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SLACK_UNEXPECTED_TOKEN"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Slack rejected the payload due to invalid syntax."
            }), _jsx(_components.td, {
              align: "left",
              children: "Review templated message content and JSON formatting for syntax errors."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "other-destinations",
        children: "Other destinations"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Error code"
            }), _jsx(_components.th, {
              align: "left",
              children: "What it means"
            }), _jsx(_components.th, {
              align: "left",
              children: "How to resolve"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "AMPLITUDE_INVALID_JSON_REQUEST"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Amplitude rejected the request payload format."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Review field mappings and ensure the JSON structure matches ", _jsx(_components.a, {
                href: "https://www.docs.developers.amplitude.com/analytics/apis/http-v2-api/",
                children: "Amplitude's API requirements"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "CITRUS_AD_INVALID_BASE_URL"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The configured CitrusAd base URL is invalid."
            }), _jsx(_components.td, {
              align: "left",
              children: "Update the destination with the correct API endpoint."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "ITERABLE_INVALID_LIST_ID"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The Iterable list ID is invalid."
            }), _jsx(_components.td, {
              align: "left",
              children: "Confirm the list still exists in Iterable and the configured ID is correct."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "ANAPLAN_BAD_MAPPINGS"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The Anaplan field mappings are invalid."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Re-check dimension, line-item, and field mapping setup. See ", _jsx(_components.a, {
                href: "/destinations/anaplan#tips-and-troubleshooting",
                children: "Anaplan: Tips and troubleshooting"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "ANAPLAN_NULL_POINTER"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The Anaplan request failed with a null-pointer error."
            }), _jsx(_components.td, {
              align: "left",
              children: "Retry first. If it persists, check mapped objects and source values for missing required fields."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "TABOOLA_MISSING_NUMERIC_ACCOUNT_ID"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The Taboola account ID is missing or not numeric."
            }), _jsx(_components.td, {
              align: "left",
              children: "Update the destination configuration with the correct numeric account ID."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "ELASTICSEARCH_INVALID_METADATA_TYPE"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The Elasticsearch metadata type is invalid."
            }), _jsx(_components.td, {
              align: "left",
              children: "Check mapping configuration and ensure metadata fields use supported types."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "FRESHDESK_CLOUDFLARE_IP_BLOCKED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Freshdesk's Cloudflare proxy blocked the request IP."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Confirm ", _jsx(_components.a, {
                href: "/security/networking#ip-addresses",
                children: "Hightouch's static IPs"
              }), " are allowlisted, or contact Freshdesk support."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "rejected-row-errors",
        children: "Rejected-row errors"
      }), "\n", _jsxs(_components.p, {
        children: ["Rejected-row errors apply to individual records, not the entire sync run. You can inspect rejected rows in the ", _jsx(_components.a, {
          href: "/syncs/debugger",
          children: "live debugger"
        }), " or ", _jsx(_components.a, {
          href: "/syncs/warehouse-sync-logs",
          children: "warehouse sync logs"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Error code"
            }), _jsx(_components.th, {
              align: "left",
              children: "What it means"
            }), _jsx(_components.th, {
              align: "left",
              children: "How to resolve"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "NULL_EXTERNAL_ID"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "A required external ID is null for one or more rows."
            }), _jsx(_components.td, {
              align: "left",
              children: "Ensure the mapped identifier column is populated for every record. Update your model to filter out rows with null identifiers."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "DUPLICATE_PRIMARY_KEY"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Multiple source rows share the same primary key value."
            }), _jsx(_components.td, {
              align: "left",
              children: "Deduplicate your model so each primary key appears only once per sync run."
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "linkedin",
        children: "LinkedIn"
      }), "\n", _jsxs(_components.p, {
        children: ["For additional LinkedIn-specific errors, see ", _jsx(_components.a, {
          href: "/destinations/linkedin#tips-and-troubleshooting",
          children: "LinkedIn: Tips and troubleshooting"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Error code"
            }), _jsx(_components.th, {
              align: "left",
              children: "What it means"
            }), _jsx(_components.th, {
              align: "left",
              children: "How to resolve"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "LINKEDIN_INVALID_CONVERSION"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "LinkedIn rejected the conversion payload."
            }), _jsx(_components.td, {
              align: "left",
              children: "Check that all required conversion fields are present and correctly formatted."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "LINKEDIN_EVENT_TOO_OLD"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The event timestamp is outside LinkedIn's allowed time window."
            }), _jsx(_components.td, {
              align: "left",
              children: "Send more recent events. LinkedIn typically requires events within the last 90 days."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "LINKEDIN_MISSING_USER_IDS"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Required LinkedIn user identifiers are missing."
            }), _jsx(_components.td, {
              align: "left",
              children: "Map at least one accepted identifier (email, Google Advertising ID, or Apple IDFA) for each row."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "LINKEDIN_MISSING_INVALID_USER_INFO"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "LinkedIn user information is missing or invalid."
            }), _jsx(_components.td, {
              align: "left",
              children: "Review identifier formatting and required match fields."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "LINKEDIN_MISSING_IDENTIFIER_OR_NAME"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "A required identifier or name value is missing from the row."
            }), _jsx(_components.td, {
              align: "left",
              children: "Populate the required matching fields before syncing."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "LINKEDIN_INVALID_SEGMENT_VALUE"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "A segment value (often email) failed LinkedIn's validation."
            }), _jsx(_components.td, {
              align: "left",
              children: "Check email formatting and ensure values meet LinkedIn's requirements."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "LINKEDIN_PERMISSION_DENIED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The connected LinkedIn account lacks the required permissions."
            }), _jsx(_components.td, {
              align: "left",
              children: "Reauthorize with a LinkedIn account that can manage the target audience or conversion action."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "LINKEDIN_API_ERROR"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "LinkedIn returned a generic row-level API failure."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Retry if the issue appears transient. Use the ", _jsx(_components.a, {
                href: "/syncs/debugger",
                children: "live debugger"
              }), " to inspect LinkedIn's response for the affected rows."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.h2, {
        id: "predictive-traits-errors",
        children: "Predictive traits errors"
      }), "\n", _jsxs(_components.p, {
        children: ["These errors occur when training or running ", _jsx(_components.a, {
          href: "/customer-studio/predictive-traits",
          children: "predictive trait"
        }), " models. They indicate a problem with training data, event configuration, or warehouse access."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              align: "left",
              children: "Error code"
            }), _jsx(_components.th, {
              align: "left",
              children: "What it means"
            }), _jsx(_components.th, {
              align: "left",
              children: "How to resolve"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "NO_USERS_IN_INPUT_FILE"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The training audience returned no users."
            }), _jsx(_components.td, {
              align: "left",
              children: "Check that the parent audience or model query returns rows before training starts."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "NO_USER_ROWS_AFTER_FILTERS"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Users were present initially, but predictive filters removed all of them."
            }), _jsx(_components.td, {
              align: "left",
              children: "Loosen the filters or verify that event and property conditions match real data."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "NO_TRAINING_DATA_FILES"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Hightouch couldn't generate the training dataset."
            }), _jsx(_components.td, {
              align: "left",
              children: "Confirm that the configured events return data and the event model is queryable."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "LABEL_FREQUENCY_TOO_LOW"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "There aren't enough examples of the target outcome to train the model reliably."
            }), _jsx(_components.td, {
              align: "left",
              children: "Increase the lookback window, reduce filters, or choose a higher-volume outcome event."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "NO_POSITIVE_EXAMPLES"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The training data contains zero positive examples of the target event."
            }), _jsx(_components.td, {
              align: "left",
              children: "Check that the outcome event is occurring for users in scope."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "TRAINING_MATRIX_DOWNLOAD_FAILED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "An internal failure occurred while retrieving the training artifact."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Retry the run. If it persists, ", _jsx(PylonLink, {
                children: "contact support"
              }), " — this is typically not customer-fixable."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "NO_KEY_EVENTS"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "No feature-generation events are configured."
            }), _jsx(_components.td, {
              align: "left",
              children: "Add at least one event input in the predictive trait configuration before retraining."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "EVENT_MODEL_MISCONFIGURED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The event model configuration is invalid."
            }), _jsx(_components.td, {
              align: "left",
              children: "Confirm the event model exists, has a valid primary key, and includes all referenced columns."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "EVENT_QUERY_FAILED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Hightouch couldn't query event data from the warehouse."
            }), _jsx(_components.td, {
              align: "left",
              children: "Check warehouse permissions, model SQL, and whether the referenced tables still exist."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "SOURCE_ACCOUNT_LOCKED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "The warehouse user account is locked."
            }), _jsx(_components.td, {
              align: "left",
              children: "Wait for the lockout to clear, or have your warehouse admin re-enable the account."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "UNKNOWN_EVENT_IN_FILTER"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "A predictive filter references an event Hightouch can't resolve."
            }), _jsx(_components.td, {
              align: "left",
              children: "Check for renamed, deleted, or misconfigured events in the predictive trait setup."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "INVALID_EVENT_PROPERTY_CONDITION"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "An event-property filter is malformed."
            }), _jsx(_components.td, {
              align: "left",
              children: "Verify the filter syntax and confirm you're using valid string property names."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "NO_EVENT_DATA_FILES"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "No event data was produced for training or inference."
            }), _jsx(_components.td, {
              align: "left",
              children: "Confirm the configured events have recent data and event pulls are succeeding."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "TRAINING_ERROR"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "An internal training failure occurred."
            }), _jsxs(_components.td, {
              align: "left",
              children: ["Retry once. If it repeats, ", _jsx(PylonLink, {
                children: "contact support"
              }), "."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "CACHED_EVENT_PULL_DISABLED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "A required event pull is disabled."
            }), _jsx(_components.td, {
              align: "left",
              children: "Re-enable the event pull in your schema configuration and rerun the predictor."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "MALFORMED_EVENT_DATA"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Numeric event fields contain invalid values."
            }), _jsx(_components.td, {
              align: "left",
              children: "Clean the source data so numeric columns contain only valid numbers (no strings, nulls in numeric-only fields, etc.)."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              align: "left",
              children: _jsx(_components.code, {
                children: "MODEL_QUERY_FAILED"
              })
            }), _jsx(_components.td, {
              align: "left",
              children: "Hightouch couldn't query the parent model data needed for the run."
            }), _jsx(_components.td, {
              align: "left",
              children: "Check that the parent model still exists and all referenced columns are present."
            })]
          })]
        })]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
